import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { QmAttachment, QmCrudStatus, QmRecord } from '../../api/qmCrud';
import { StyledButton } from '../../components';
import QmHistoryTimeline from './QmHistoryTimeline';

export type OptionItem = {
  code: string;
  label: string;
};

export type QmDetailDialogModel = {
  selectedQm: QmRecord | null;
  isDetailsLoading: boolean;
  canEdit: boolean;
  sectionCardSx: Record<string, unknown>;
  imageAttachments: QmAttachment[];
  isAttachmentsLoading: boolean;
  onSelectPreview: (preview: { name: string; url: string }) => void;
  actionComment: string;
  onActionCommentChange: (value: string) => void;
  addActionComment: () => void;
  editStatus: QmCrudStatus;
  onEditStatusChange: (status: QmCrudStatus) => void;
  editClosureCode: string;
  onEditClosureCodeChange: (value: string) => void;
  editClosureNotes: string;
  onEditClosureNotesChange: (value: string) => void;
  saveQmEdits: () => void;
  closureOptions: OptionItem[];
  reasonOptions: OptionItem[];
  getLocationLabel: (itemLocation: string | null | undefined) => string;
  statusOptions: QmCrudStatus[];
  formatStatusLabel: (status: QmCrudStatus | string | null | undefined) => string;
};

type QmDetailDialogProps = {
  open: boolean;
  onClose: () => void;
  model: QmDetailDialogModel;
};

export default function QmDetailDialog({ open, onClose, model }: QmDetailDialogProps) {
  const {
    selectedQm,
    isDetailsLoading,
    canEdit,
    sectionCardSx,
    imageAttachments,
    isAttachmentsLoading,
    onSelectPreview,
    actionComment,
    onActionCommentChange,
    addActionComment,
    editStatus,
    onEditStatusChange,
    editClosureCode,
    onEditClosureCodeChange,
    editClosureNotes,
    onEditClosureNotesChange,
    saveQmEdits,
    closureOptions,
    reasonOptions,
    getLocationLabel,
    statusOptions,
    formatStatusLabel
  } = model;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: { xs: '94vh', md: '90vh' }
        }
      }}>
      <Stack spacing={1.5} sx={{ p: { xs: 1.25, md: 1.75 }, overflowY: 'auto', bgcolor: '#fafbfc' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.6}>
            {selectedQm ? (
              <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="h6">{`QM ${selectedQm.qmNumber}`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  QM Type: {selectedQm.type || '-'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="h6">QM Details</Typography>
            )}
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {!selectedQm || isDetailsLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {!canEdit ? (
              <Alert severity="info">Read-only role detected. Edit and comment actions are disabled.</Alert>
            ) : null}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
              <Stack spacing={1.25} sx={{ flex: 1.2 }}>
                <Paper variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    QM Details
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 1
                    }}>
                    {[
                      { label: 'Reason For QM', value: selectedQm.reasonLabel || '-' },
                      {
                        label: 'SSCC / ASN',
                        value:
                          selectedQm.sscc && selectedQm.asn
                            ? `${selectedQm.sscc} / ${selectedQm.asn}`
                            : selectedQm.sscc || selectedQm.asn || '-'
                      },
                      { label: 'Purchase Order', value: selectedQm.poNumber || '-' },
                      { label: 'Purchase Order Line', value: selectedQm.poLine || '-' },
                      { label: 'PO Line ID', value: selectedQm.poLineId || '-' },
                      { label: 'Item ID', value: selectedQm.itemId || '-' },
                      { label: 'Supplier', value: selectedQm.supplierName || '-' },
                      { label: 'Contact', value: selectedQm.contactName || '-' },
                      { label: 'Additional Details', value: selectedQm.otherInformation || '-' },
                      { label: 'Item Location', value: getLocationLabel(selectedQm.itemLocation) },
                      { label: 'Number of Packages', value: selectedQm.packageCount?.toString() || '-' },
                      { label: 'Package Types', value: selectedQm.packageType || '-' },
                      { label: 'Description / Comments', value: selectedQm.description || '-' },
                      { label: 'Closure Code', value: selectedQm.closureLabel || selectedQm.closureCode || '-' },
                      { label: 'Closure Notes', value: selectedQm.closureNotes || '-' },
                      { label: 'Created', value: dayjs(selectedQm.createdAt).format('DD/MM/YYYY HH:mm') }
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          px: 1,
                          py: 0.85,
                          borderRadius: 1.25,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.default'
                        }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', mb: 0.2 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.3 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>

                <Paper variant="outlined" sx={sectionCardSx}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Pictures</Typography>
                    <Chip size="small" label={`${imageAttachments.length} images`} variant="outlined" />
                  </Stack>
                  {isAttachmentsLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </Stack>
                  ) : imageAttachments.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap={1.1}>
                      {imageAttachments.map((attachment) => (
                        <Box
                          key={attachment.id}
                          onClick={() => onSelectPreview({ name: attachment.fileName, url: attachment.downloadUrl })}
                          sx={{
                            width: { xs: 90, md: 104 },
                            height: { xs: 90, md: 104 },
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              transform: 'translateY(-1px)',
                              boxShadow: 2
                            }
                          }}>
                          <Box
                            component="img"
                            src={attachment.downloadUrl}
                            alt={attachment.fileName}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No picture attachments found for this QM.
                    </Typography>
                  )}
                </Paper>
              </Stack>

              <Stack spacing={1.25} sx={{ flex: 1 }}>
                <Paper variant="outlined" sx={sectionCardSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Coordinator Actions
                  </Typography>
                  <Stack
                    spacing={1}
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '0.86rem' },
                      '& .MuiInputLabel-root': { fontSize: '0.86rem' },
                      '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
                    }}>
                    <TextField
                      size="small"
                      label="Action Comment"
                      value={actionComment}
                      onChange={(event) => onActionCommentChange(event.target.value)}
                      multiline
                      rows={2}
                      disabled={!canEdit}
                    />
                    <StyledButton
                      variant="outlined"
                      onClick={addActionComment}
                      disabled={!canEdit || !actionComment.trim()}>
                      Add Action History
                    </StyledButton>

                    <Divider />

                    <TextField
                      size="small"
                      select
                      label="Status"
                      value={editStatus}
                      onChange={(event) => onEditStatusChange(event.target.value as QmCrudStatus)}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {formatStatusLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      size="small"
                      select
                      label="Closure Code"
                      value={editClosureCode}
                      onChange={(event) => onEditClosureCodeChange(event.target.value)}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                      <MenuItem value="">None</MenuItem>
                      {closureOptions.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      size="small"
                      label="Closure Notes"
                      value={editClosureNotes}
                      onChange={(event) => onEditClosureNotesChange(event.target.value)}
                      multiline
                      rows={2}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}
                    />
                    <StyledButton
                      variant="contained"
                      onClick={saveQmEdits}
                      disabled={!canEdit || selectedQm.status === 'CLOSED'}>
                      Save QM Changes
                    </StyledButton>
                  </Stack>
                </Paper>

                <QmHistoryTimeline
                  selectedQm={selectedQm}
                  reasonOptions={reasonOptions}
                  sectionCardSx={sectionCardSx}
                  formatStatusLabel={formatStatusLabel}
                />
              </Stack>
            </Stack>

            <Divider />
            <Stack direction="row" justifyContent="flex-end">
              <StyledButton variant="outlined" onClick={onClose}>
                Close
              </StyledButton>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
